import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import { Button, Dropdown, Modal, Typography } from "antd";
import {
  EXPORT_CNF_BILL_DELETE_ENDPOINT,
  EXPORT_CNF_BILL_LIST_ENDPOINT,
} from "apiServices/API_ENDPOINTS";
import { deleteData, getData } from "apiServices/common";
import AppContent from "common/AppContent";
import ListTable from "common/ListTable";
import AppPageHeader from "common/PageHeader";
import ExtraContent from "components/Configuration/CommonComponents/ExtraContent";
import FilterComponent from "components/Configuration/CommonComponents/FilterComponent";
import { getExportCnfBillRowTotal } from "components/ImportCustomsClearance/ExternalCnfBills/PrepareBilling/cnfBillAmount";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  EXTERNAL_CNF_BILLS_CREATE_PERMISSION,
  EXTERNAL_CNF_BILLS_DELETE_PERMISSION,
  EXTERNAL_CNF_BILLS_EDIT_PERMISSION,
  EXTERNAL_CNF_BILLS_VIEW_PERMISSION,
} from "routes/permissions";
import { hasPermission } from "utils/functions";

const formatDate = (value) =>
  value && moment(value).isValid() ? moment(value).format("DD-MMM-YYYY") : "—";

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const getLcOrSalesContractNo = (invoice) =>
  invoice?.sc_commercial_lc_info?.export_lc_no ||
  invoice?.sc_commercial_lc_value ||
  invoice?.sales_contract_info?.reference_no ||
  "—";

export default function ExportCnfBillList() {
  const location = useLocation();
  const navigate = useNavigate();
  const routerPath = location?.pathname;

  const [showFilter, setShowFilter] = useState(
    localStorage.getItem("filter_status") && routerPath
      ? JSON.parse(localStorage.getItem("filter_status"))[routerPath]
      : false,
  );
  const [refresh, setRefresh] = useState(0);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [metaData, setMetaData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const { Title } = Typography;
  const filterData = useSelector(({ filter }) => filter.filterData);

  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsed = filterStatus ? JSON.parse(filterStatus) : {};
    localStorage.setItem(
      "filter_status",
      JSON.stringify({
        ...parsed,
        [routerPath]: showFilter,
      }),
    );
  }, [showFilter, routerPath]);

  const fetchList = useCallback(
    async (filterValues) => {
      setLoading(true);
      setFetchError(null);
      const query = `${EXPORT_CNF_BILL_LIST_ENDPOINT}?page=${currentPage}&per_page=${pageSize}`;
      const bodyData = {
        ...(filterValues?.name && { invoice_number: filterValues.name }),
      };
      const response = await getData(query, false, bodyData);
      if (response?.status === 200) {
        const data = response.data?.data?.data || [];
        setTableData(
          data.map((item, index) => ({
            ...item,
            key: item.id || `ex-cnf-${index}`,
          })),
        );
        setMetaData({
          current: response.data?.data?.current_page || 1,
          defaultPageSize: response.data?.data?.per_page || 10,
          total: response.data?.data?.total || 0,
        });
      } else {
        setFetchError("Could not load this list.");
      }
      setLoading(false);
    },
    [currentPage, pageSize],
  );

  useEffect(() => {
    fetchList(filterData);
  }, [refresh, filterData, fetchList]);

  const refetch = useCallback(() => setRefresh((p) => p + 1), []);

  const onDelete = useCallback(
    (id) => {
      Modal.confirm({
        title: "Confirm deletion",
        content: "Delete this Export C&F bill?",
        okType: "danger",
        onOk: async () => {
          const res = await deleteData(`${EXPORT_CNF_BILL_DELETE_ENDPOINT}/${id}`);
          if (res && (res?.code === 200 || res?.status === 200)) {
            refetch();
          }
        },
      });
    },
    [refetch],
  );

  const columns = useMemo(
    () => [
      {
        title: "Invoice Number",
        key: "inv",
        render: (_, r) =>
          r.export_invoice?.invoice_number || r.invoice_number || "—",
      },
      {
        title: "LC/Sales Contract Number",
        key: "lc_sc",
        render: (_, r) => getLcOrSalesContractNo(r.export_invoice),
      },
      {
        title: "Invoice Quantity",
        key: "qty",
        align: "right",
        render: (_, r) => r.export_invoice?.total_quantity ?? "—",
      },
      {
        title: "Quantity Unit",
        key: "qty_unit",
        render: (_, r) => (r.export_invoice?.total_quantity != null ? "Pcs" : "—"),
      },
      {
        title: "Invoice Value",
        key: "val",
        align: "right",
        render: (_, r) =>
          r.export_invoice?.invoice_value != null
            ? formatAmount(r.export_invoice.invoice_value)
            : "—",
      },
      {
        title: "Total Bill Amount",
        key: "total_bill_amount",
        align: "right",
        render: (_, r) => formatAmount(getExportCnfBillRowTotal(r)),
      },
      {
        title: "Bill of Export Date",
        key: "boe_date",
        render: (_, r) => formatDate(r.export_invoice?.boe_date),
      },
      {
        title: "Bill Create Date",
        dataIndex: "created_at",
        render: (v) => formatDate(v),
      },
      {
        title: "Bill Number",
        dataIndex: "bill_no",
        render: (v) => v || "—",
      },
      {
        title: "Action",
        key: "action",
        width: 120,
        render: (_, record) => (
          <Dropdown
            menu={{
              items: [
                hasPermission([EXTERNAL_CNF_BILLS_VIEW_PERMISSION]) && {
                  key: "view",
                  label: "View",
                  icon: <EyeOutlined />,
                  onClick: () =>
                    navigate(
                      `/import-export-expense-tracking/external-cnf-bills/export-cnf-bill/view/${record.id}`,
                    ),
                },
                hasPermission([EXTERNAL_CNF_BILLS_EDIT_PERMISSION]) && {
                  key: "edit",
                  label: "Edit",
                  icon: <EditOutlined />,
                  onClick: () =>
                    navigate(
                      `/import-export-expense-tracking/external-cnf-bills/export-cnf-bill/edit/${record.id}`,
                    ),
                },
                hasPermission([EXTERNAL_CNF_BILLS_DELETE_PERMISSION]) && {
                  key: "del",
                  label: "Delete",
                  icon: <DeleteOutlined />,
                  danger: true,
                  onClick: () => onDelete(record.id),
                },
              ].filter(Boolean),
            }}
          >
            <Button type="link" icon={<MoreOutlined />} />
          </Dropdown>
        ),
      },
    ],
    [navigate, onDelete],
  );

  const handleTableChange = (pagination) => {
    if (pagination.current !== currentPage) setCurrentPage(pagination.current);
    if (pagination.pageSize !== pageSize) {
      setPageSize(pagination.pageSize);
      setCurrentPage(1);
    }
  };

  return (
    <AppContent
      header={
        <AppPageHeader
          extra={
            <ExtraContent
              refresh={refresh}
              setRefresh={setRefresh}
              handleAddItemOpen={() =>
                navigate(
                  "/import-export-expense-tracking/external-cnf-bills/export-cnf-bill/add",
                )
              }
              disabled={!hasPermission([EXTERNAL_CNF_BILLS_CREATE_PERMISSION])}
              addButtonText={
                hasPermission([EXTERNAL_CNF_BILLS_CREATE_PERMISSION])
                  ? "Add New"
                  : "No permission"
              }
              showFilter={showFilter}
              setShowFilter={setShowFilter}
            />
          }
          title={<Title level={5}>Export C&F Bill</Title>}
        />
      }
      content={
        <>
          <FilterComponent
            tableData={tableData}
            filterAPI={fetchList}
            showFilter={showFilter}
            setShowFilter={setShowFilter}
          />
          <div className="table" style={{ marginTop: 10 }}>
            <ListTable
              loading={loading}
              error={fetchError}
              onRetry={() => fetchList(filterData)}
              dataSource={tableData}
              columns={columns}
              rowKey="id"
              pagination={{
                current: currentPage,
                pageSize,
                total: metaData?.total || 0,
                showSizeChanger: true,
                showTotal: (t) => `Total ${t} items`,
              }}
              onChange={handleTableChange}
              bordered
              scroll={{ x: "max-content" }}
            />
          </div>
        </>
      }
    />
  );
}
